import { Form, InputNumber, Input, Switch } from 'antd'
import { Flex, Heading, Box, borderRadii } from '@weareberyl/design-system'

import { VEHICLE_CATEGORY_LABELS, VEHICLE_TYPES } from '~/constants/domain'
import { VehicleCategory, ProductType } from '~/gql/generated/graphql'
import { VehicleIcon } from '~/utils'
import { CurrencyInput } from '~/components/shared/numberInputs'
import {
  currencyFormItemConversionProps,
  isAPeriodPass,
  isASubscriptionPass,
} from './utils'
import useCompareOtherSchemeProducts from './useCompareOtherSchemeProducts'

type Props = {
  category: VehicleCategory
  productType: ProductType
}

const ProductPricingConfigForm = ({ category, productType }: Props) => {
  const vehicleType = VEHICLE_TYPES[category]

  const isPeriodPass = isAPeriodPass(productType)
  const isSubscriptionPass = isASubscriptionPass(productType)

  const form = Form.useFormInstance()
  const enabled = Form.useWatch(['configs_v3', category, 'enabled'], form)
  const borderColor = enabled ? 'jade' : 'haze'
  const backgroundColor = enabled ? 'white' : 'oyster'

  const schemeId = '3'
  const { getOtherValues } = useCompareOtherSchemeProducts({
    schemeId,
    productType,
  })

  return (
    <Box p={0} style={{ gap: '20px' }}>
      <Flex style={{ gap: '1ex' }}>
        <Form.Item name={['configs_v3', category, 'enabled']} noStyle>
          <Switch checked={enabled} />
        </Form.Item>
        <VehicleIcon size={20} vehicle={vehicleType} />
        <Heading variant="h4">{VEHICLE_CATEGORY_LABELS[category]}</Heading>
      </Flex>

      <Box
        borderWidth={2}
        px={3}
        pt={2}
        borderColor={borderColor}
        backgroundColor={backgroundColor}
        borderRadius={borderRadii.md}
      >
        <Heading variant="h5" pt={2} pb={3}>
          Pricing
        </Heading>

        <Form.Item
          name={['configs_v3', category, 'unlock_fee']}
          label="Unlock fee"
          extra={getOtherValues(['configs_v3', category, 'unlock_fee'])}
          {...currencyFormItemConversionProps}
        >
          <CurrencyInput disabled={!enabled} />
        </Form.Item>

        {productType === ProductType.payg && (
          <>
            <Form.Item
              name={['configs_v3', category, 'time_increment_cost']}
              label="PaYG Increment cost"
              tooltip="Price per increment of time, probably a minute"
            >
              <InputNumber
                min={0}
                step={1}
                addonAfter={'p'}
                disabled={!enabled}
              />
            </Form.Item>
            <Form.Item
              name={['configs_v3', category, 'time_increment_cost_weekend']}
              label="(Weekend) PaYG Increment cost"
              tooltip="Price per increment of time, probably a minute"
            >
              <InputNumber
                min={0}
                step={1}
                addonAfter={'p'}
                disabled={!enabled}
              />
            </Form.Item>
            <Form.Item
              name={['configs_v3', category, 'charge_increment_duration']}
              label="... per increment duration"
              tooltip="ISO8607 e.g. 'PT1M'"
              initialValue="PT1M"
            >
              <Input disabled />
            </Form.Item>
            <Form.Item
              name={['configs_v3', category, 'journey_minutes_included']}
              label="Journey minutes included"
              tooltip="For fixed pricing, minute rate starts after a fixed initial period."
            >
              <InputNumber
                min={0}
                step={10}
                addonAfter={'mins'}
                disabled={!enabled}
              />
            </Form.Item>
          </>
        )}

        {isSubscriptionPass && (
          <Form.Item
            name={['configs_v3', category, 'journey_minutes_included']}
            label="Journey minutes included"
            tooltip="Included minutes per journey. On longer journeys, other passes are charged for remainder."
          >
            <InputNumber
              min={0}
              step={10}
              addonAfter={'mins'}
              disabled={!enabled}
            />
          </Form.Item>
        )}
      </Box>

      {isPeriodPass && (
        <Box
          borderWidth={2}
          px={3}
          pt={2}
          borderColor={borderColor}
          backgroundColor={backgroundColor}
          borderRadius={borderRadii.md}
        >
          <Heading variant="h5" pb={3} pt={2}>
            Caps
          </Heading>
          <Form.Item
            name={['configs_v3', category, 'journey_minutes_percent_off']}
            label="Journey minutes % off"
            tooltip="Use this to discount the (effective) minute rate for this vehicle type. e.g. if a pedal bike should cost less but doesn’t have a lower unlock fee. The percentage sets how much of the minute balance is used (e.g., 50% for half-rate minutes)."
            extra={getOtherValues([
              'configs_v3',
              category,
              'journey_minutes_percent_off',
            ])}
          >
            <InputNumber
              min={0}
              max={100}
              step={10}
              addonAfter={'%'}
              disabled={!enabled}
            />
          </Form.Item>
          <Form.Item
            name={['configs_v3', category, 'period_pass_max_minute_debit']}
            label="Max minute debit"
            tooltip="Max number of minutes that can be debited from this pass for one journey."
            extra={getOtherValues([
              'configs_v3',
              category,
              'period_pass_max_minute_debit',
            ])}
          >
            <InputNumber
              min={0}
              step={10}
              addonAfter={'min'}
              disabled={!enabled}
            />
          </Form.Item>
        </Box>
      )}
    </Box>
  )
}

export default ProductPricingConfigForm
